import { useState } from "react";
import type { RoundAssignment, RoundGroup } from "../../domain/round/types";
import type { RoundWorkspacePayload } from "../../lib/api";
import { exportOperationalScoringPng, exportOperationalTransportPng, exportReserveChangesPng, type ReserveChangeRow } from "./operational-export";
import { RESERVE_PEOPLE, RESERVE_PLANS } from "./reserve-plans";

type Kind = "scoring" | "changes" | "arrival" | "return";

const SEGMENT_LABEL: Record<string, string> = { full18: "18 dołków", holes1to9: "dołki 1–9", holes10to18: "dołki 10–18" };
const clock = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

function interval(group: RoundGroup, segment: string): [number, number] {
  const start = group.startMinutes, f9 = group.finish9Minutes ?? start, f18 = group.finish18Minutes ?? start;
  if (segment === "holes1to9") return [start, f9];
  if (segment === "holes10to18") return [f9, f18];
  return [start, f18];
}

function planKey(absent: string[]) {
  return RESERVE_PEOPLE.filter((p) => absent.includes(p.id)).map((p) => p.id).join("+");
}

export function buildReserve(data: RoundWorkspacePayload, absent: string[]) {
  const plan = RESERVE_PLANS[planKey(absent)];
  if (!plan || !data.schedule) return null;
  const copy: RoundWorkspacePayload = JSON.parse(JSON.stringify(data));
  const schedule = copy.schedule!;
  const names = new Map(data.schedule!.people.map((p) => [p.personId, p.displayName]));
  const groups = new Map(schedule.groups.map((g) => [g.id, g]));
  const oldByGroup = new Map<string, RoundAssignment[]>();
  for (const a of data.schedule!.assignments) oldByGroup.set(a.groupId, [...(oldByGroup.get(a.groupId) || []), a]);

  // Które przydziały są nowe (nie było takiej trójki grupa+zakres+osoba w zatwierdzonym R2).
  const oldKeys = new Map<string, number>();
  for (const a of data.schedule!.assignments) { const k = `${a.groupId}|${a.segment}|${a.personId}`; oldKeys.set(k, (oldKeys.get(k) || 0) + 1); }
  const changedNew: Array<{ groupId: string; segment: string; personId: string }> = [];
  const newAssignments = plan.assignments.map(([groupId, segment, personId]) => {
    const k = `${groupId}|${segment}|${personId}`;
    const left = oldKeys.get(k) || 0;
    const isNew = left <= 0;
    if (!isNew) oldKeys.set(k, left - 1); else changedNew.push({ groupId, segment, personId });
    return { id: `reserve:${groupId}:${segment}:${personId}`, locked: false, source: (isNew ? "reserve" : "generated") as RoundAssignment["source"], groupId, segment: segment as RoundAssignment["segment"], personId };
  });
  // Zatwierdzone przydziały, których w rezerwie już nie ma (per grupa).
  const removedByGroup = new Map<string, Array<{ segment: string; personId: string }>>();
  for (const [k, count] of oldKeys) {
    if (count <= 0) continue;
    const [groupId, segment, personId] = k.split("|");
    for (let i = 0; i < count; i += 1) removedByGroup.set(groupId, [...(removedByGroup.get(groupId) || []), { segment, personId }]);
  }
  const rows: ReserveChangeRow[] = [];
  const usedRemoved = new Map<string, number>();
  for (const item of changedNew) {
    const group = groups.get(item.groupId)!;
    const removed = removedByGroup.get(item.groupId) || [];
    const index = usedRemoved.get(item.groupId) || 0;
    const prev = removed.find((r, i) => i >= index && r.segment === item.segment) || removed[index];
    const prevIndex = prev ? removed.indexOf(prev) : -1;
    if (prevIndex >= 0) { removed.splice(prevIndex, 1); }
    const [from, to] = interval(group, item.segment);
    rows.push({
      group: group.number, tee: group.tee, time: `${clock(from)}–${clock(to)}`, segment: SEGMENT_LABEL[item.segment] || item.segment, tv: group.tv,
      was: prev ? `${names.get(prev.personId) || prev.personId}${absent.includes(prev.personId) ? " (nieobecny)" : ""}${prev.segment !== item.segment ? ` · ${SEGMENT_LABEL[prev.segment] || prev.segment}` : ""}` : "—",
      now: names.get(item.personId) || item.personId,
    });
  }
  for (const [groupId, removed] of removedByGroup) {
    const group = groups.get(groupId)!;
    for (const r of removed) {
      const [from, to] = interval(group, r.segment);
      rows.push({ group: group.number, tee: group.tee, time: `${clock(from)}–${clock(to)}`, segment: SEGMENT_LABEL[r.segment] || r.segment, tv: group.tv, was: `${names.get(r.personId) || r.personId}${absent.includes(r.personId) ? " (nieobecny)" : ""}`, now: "— zdjęta obsada" });
    }
  }
  rows.sort((a, b) => a.time.localeCompare(b.time) || a.group - b.group);

  schedule.assignments = newAssignments as typeof schedule.assignments;
  schedule.groups = schedule.groups.map((group) => plan.released.includes(group.id) ? { ...group, tv: false, teePin: false, requiredScorers: 1, coverageScorers: 1 } : group);
  schedule.people = schedule.people.filter((person) => !absent.includes(person.personId));
  if (copy.transport) {
    const strip = (trips: typeof copy.transport.departures) => trips
      .map((trip) => ({ ...trip, passengerPersonIds: trip.passengerPersonIds.filter((id) => !absent.includes(id)) }))
      .filter((trip) => trip.passengerPersonIds.length > 0 || !absent.includes(trip.driverPersonId));
    copy.transport = { ...copy.transport, departures: strip(copy.transport.departures), returns: strip(copy.transport.returns) };
  }

  // Zmiana godzin pracy względem zatwierdzonego planu.
  const duty: Array<{ name: string; was: string; now: string; earlier: boolean }> = [];
  const byPerson = new Map<string, Array<[number, number]>>();
  for (const a of newAssignments) byPerson.set(a.personId, [...(byPerson.get(a.personId) || []), interval(groups.get(a.groupId)!, a.segment)]);
  for (const person of data.schedule!.people) {
    const spans = byPerson.get(person.personId);
    if (!spans?.length || person.dutyStartMinutes == null || person.dutyEndMinutes == null) continue;
    const start = Math.min(...spans.map((s) => s[0])), end = Math.max(...spans.map((s) => s[1]));
    if (start !== person.dutyStartMinutes || end !== person.dutyEndMinutes) duty.push({ name: person.displayName, was: `${clock(person.dutyStartMinutes)}–${clock(person.dutyEndMinutes)}`, now: `${clock(start)}–${clock(end)}`, earlier: start < person.dutyStartMinutes || end > person.dutyEndMinutes });
  }
  duty.sort((a, b) => a.name.localeCompare(b.name, "pl"));

  const released = plan.released.map((id) => {
    const group = groups.get(id)!;
    return group.teePin ? `Tee/Pin grupa ${group.number}: zdjęta druga obsada (zostaje 1 osoba)` : `Grupa ${group.number}: bez TV, pojedyncza obsada`;
  });
  return { workspace: copy, rows, duty, released };
}

export function ReservePlanPanel({ data, tournamentName }: { data: RoundWorkspacePayload; tournamentName: string }) {
  const [absent, setAbsent] = useState<string[]>([]);
  const [busy, setBusy] = useState<Kind | "">("");
  const [error, setError] = useState("");
  const reserve = absent.length ? buildReserve(data, absent) : null;
  const absentNames = RESERVE_PEOPLE.filter((p) => absent.includes(p.id)).map((p) => p.name);
  const label = `${tournamentName} — PLAN REZERWOWY`;

  async function run(kind: Kind) {
    if (!reserve) return;
    setError(""); setBusy(kind);
    try {
      if (kind === "scoring") await exportOperationalScoringPng(reserve.workspace, `${label} (brak: ${absentNames.join(", ")})`);
      if (kind === "changes") await exportReserveChangesPng(tournamentName, data.round, absentNames, reserve.released, reserve.rows, reserve.duty);
      if (kind === "arrival") await exportOperationalTransportPng(reserve.workspace, label, "to-course");
      if (kind === "return") await exportOperationalTransportPng(reserve.workspace, label, "return");
    } catch (exc) { setError(exc instanceof Error ? exc.message : String(exc)); } finally { setBusy(""); }
  }

  return (
    <article className="panel report-round-card reserve-plan-card">
      <header><div><strong>Plan rezerwowy R2</strong><span>tylko PNG, zatwierdzone R2 bez zmian</span></div><b>REZERWA</b></header>
      <p>Zaznacz, kto nie przyjdzie. Zmienione przydziały są na scoringu obwiedzione na pomarańczowo (NOWY), a osobny obraz „Zmiany” pokazuje było → jest.</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        {RESERVE_PEOPLE.map((person) => <label key={person.id} style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input type="checkbox" checked={absent.includes(person.id)} onChange={(event) => setAbsent((prev) => event.target.checked ? [...prev, person.id] : prev.filter((id) => id !== person.id))} />{person.name}
        </label>)}
      </div>
      {reserve ? <p><small>{reserve.rows.length} zmian · {reserve.released.join(" · ") || "bez zdejmowania obsad"}</small></p> : null}
      {error ? <div className="reports-export-error">{error}</div> : null}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        <button className="primary-button" type="button" disabled={!reserve || Boolean(busy)} onClick={() => void run("changes")}>{busy === "changes" ? "Tworzę…" : "Zmiany PNG"}</button>
        <button className="primary-button" type="button" disabled={!reserve || Boolean(busy)} onClick={() => void run("scoring")}>{busy === "scoring" ? "Tworzę…" : "Scoring PNG (rezerwa)"}</button>
        <button className="primary-button" type="button" disabled={!reserve || Boolean(busy)} onClick={() => void run("arrival")}>{busy === "arrival" ? "Tworzę…" : "Dojazd PNG (bez nieobecnych)"}</button>
        <button className="primary-button" type="button" disabled={!reserve || Boolean(busy)} onClick={() => void run("return")}>{busy === "return" ? "Tworzę…" : "Powrót PNG (bez nieobecnych)"}</button>
      </div>
    </article>
  );
}
